import {
  Button,
  Icons,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { WorkflowTriggerType } from '@commenda/types';
import { useReactFlow } from '@xyflow/react';
import { useMemo, useState } from 'react';
import { useEventCatalog } from '@commenda/api';
import { useWorkflow } from '../../context/WorkflowContext';
import { TSelectedEntity } from '../../types/node';

const TRIGGER_TYPE_OPTIONS = [
  { value: WorkflowTriggerType.MANUAL, label: 'Manual' },
  { value: WorkflowTriggerType.INTERNAL_EVENT, label: 'App Events' },
];

export const TriggerPropertyPanel = ({
  selectedEntity,
}: {
  selectedEntity: TSelectedEntity;
}) => {
  const { workflow, updateTrigger, setSelectedEntities } = useWorkflow();
  const { deleteElements } = useReactFlow();
  const { data: catalogData } = useEventCatalog();

  const trigger = workflow?.triggers?.find((t) => t.id === selectedEntity.id);
  const apps = useMemo(() => catalogData?.apps ?? [], [catalogData?.apps]);

  const savedErpProvider =
    trigger?.type === WorkflowTriggerType.INTERNAL_EVENT
      ? trigger.constraints?.erpProvider ?? null
      : null;

  const savedEventType =
    trigger?.type === WorkflowTriggerType.INTERNAL_EVENT
      ? trigger.eventType
      : undefined;

  const derivedApp = useMemo(() => {
    if (!savedEventType) return null;
    return (
      apps.find((a) => a.events.some((e) => e.eventType === savedEventType))
        ?.id ?? null
    );
  }, [savedEventType, apps]);

  const [selectedApp, setSelectedApp] = useState<string | null>(derivedApp);
  const [selectedErpProvider, setSelectedErpProvider] = useState<string | null>(
    savedErpProvider
  );

  if (!trigger) return null;

  const handleTypeChange = (value: string) => {
    const newType = value as WorkflowTriggerType;
    if (newType === WorkflowTriggerType.MANUAL) {
      updateTrigger(trigger.id, {
        type: WorkflowTriggerType.MANUAL,
        name: trigger.name,
      });
    } else {
      updateTrigger(trigger.id, {
        type: WorkflowTriggerType.INTERNAL_EVENT,
        name: trigger.name,
        eventType: '',
      } as any);
    }
    setSelectedApp(null);
    setSelectedErpProvider(null);
  };

  const handleNameChange = (value: string) => {
    updateTrigger(trigger.id, { name: value });
  };

  const handleAppChange = (appId: string) => {
    setSelectedApp(appId);
    setSelectedErpProvider(null);
    updateTrigger(trigger.id, {
      constraints: undefined,
    } as Record<string, unknown>);
  };

  const handleErpProviderChange = (erpProvider: string) => {
    setSelectedErpProvider(erpProvider);
    updateTrigger(trigger.id, {
      constraints: { erpProvider },
    } as Record<string, unknown>);
  };

  const handleEventTypeSelect = (eventType: string) => {
    const updates: Record<string, unknown> = { eventType };
    if (selectedErpProvider) {
      updates.constraints = { erpProvider: selectedErpProvider };
    }
    updateTrigger(trigger.id, updates);
  };

  const handleDelete = () => {
    deleteElements({ nodes: [{ id: trigger.id }] });
    setSelectedEntities([]);
  };

  const selectedAppData = apps.find((a) => a.id === selectedApp);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="txt-medium-medium">Trigger</h3>
        <Button
          variant="tertiary"
          size="sm"
          onClick={handleDelete}
        >
          <Icons.Trash01 className="size-4" />
        </Button>
      </div>

      <div className="flex flex-col gap-1">
        <Label>Name</Label>
        <Input
          value={trigger.name}
          onChange={(e) => handleNameChange(e.target.value)}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label>Trigger Type</Label>
        <Select value={trigger.type} onValueChange={handleTypeChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select trigger type" />
          </SelectTrigger>
          <SelectContent>
            {TRIGGER_TYPE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {trigger.type === WorkflowTriggerType.INTERNAL_EVENT && (
        <>
          <div className="flex flex-col gap-1">
            <Label>App</Label>
            <Select
              value={selectedApp ?? ''}
              onValueChange={handleAppChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an app" />
              </SelectTrigger>
              <SelectContent>
                {apps.map((app) => (
                  <SelectItem
                    key={app.id}
                    value={app.id}
                    disabled={app.events.length === 0}
                  >
                    {app.name}
                    {app.events.length === 0 ? ' (No events)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedAppData?.erpProviders &&
            selectedAppData.erpProviders.length > 0 && (
              <div className="flex flex-col gap-1">
                <Label>ERP Provider</Label>
                <Select
                  value={selectedErpProvider ?? ''}
                  onValueChange={handleErpProviderChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select ERP provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedAppData.erpProviders.map((provider) => (
                      <SelectItem
                        key={provider}
                        value={provider}
                        className="capitalize"
                      >
                        {provider}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

          {selectedAppData && selectedAppData.events.length > 0 && (
            <div className="flex flex-col gap-1">
              <Label>Event</Label>
              <Select
                value={
                  trigger.type === WorkflowTriggerType.INTERNAL_EVENT &&
                    'eventType' in trigger
                    ? trigger.eventType
                    : ''
                }
                onValueChange={handleEventTypeSelect}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an event" />
                </SelectTrigger>
                <SelectContent>
                  {selectedAppData.events.map((event) => (
                    <SelectItem
                      key={event.eventType}
                      value={event.eventType}
                    >
                      {event.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {trigger.type === WorkflowTriggerType.INTERNAL_EVENT &&
            'eventType' in trigger &&
            trigger.eventType && (
              <div className="flex flex-col gap-1">
                <Label>Selected Event</Label>
                <div className="p-2 rounded-md bg-bg-gray-hover">
                  <span className="txt-small-medium">
                    {trigger.eventType}
                  </span>
                </div>
              </div>
            )}

          <div className="flex flex-col gap-1">
            <Label>Filter Expression (JSONata)</Label>
            <Input
              placeholder="e.g. payload.directorName = 'John'"
              value={
                trigger.type === WorkflowTriggerType.INTERNAL_EVENT &&
                  'filterExpression' in trigger &&
                  trigger.filterExpression
                  ? trigger.filterExpression.src
                  : ''
              }
              onChange={(e) => {
                const src = e.target.value;
                if (src) {
                  updateTrigger(trigger.id, {
                    filterExpression: {
                      type: 'jsonata' as any,
                      src,
                    },
                  } as any);
                } else {
                  updateTrigger(trigger.id, {
                    filterExpression: undefined,
                  } as any);
                }
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};
